/**
 * XState v5 state machine for the voice app.
 *
 * Parallel state machine with 7 independent regions:
 * 1. request   — Chat request lifecycle (idle → thinking → streaming → idle)
 * 2. playback  — TTS playback (idle → playing → idle)
 * 3. input     — VAD/STT input (inactive → idle → speechDetected → processing → idle)
 * 4. micMode   — Mic toggle (open ↔ commandOnly)
 * 5. speakerMode — Speaker toggle (unmuted ↔ muted)
 * 6. connection — WebSocket state (disconnected ↔ connected)
 * 7. activation — App activation gate (inactive ↔ active)
 *
 * Ported from the monolithic index.html XState machine definition.
 */

import { setup, assign, stateIn, not } from 'xstate';
import { STORAGE_KEYS } from '../utils/constants';
import { readString } from '../utils/storage';

// ── Context Type ──

export interface MachineContext {
  chatGeneration: number;
  ttsInterruptedGeneration: number;
  stoppedByRemote: boolean;
  requestStartedAt: number;
  ttsQueueLength: number;
  ttsAutoPlayDeferred: boolean;
  /** Texts queued for TTS while user speech is active — flushed on SPEECH_END */
  deferredTtsTexts: string[];
  voiceInterrupt: boolean;
  graceDiscardAudio: boolean;
  _micMode: 'open' | 'commandOnly';
  _speakerMode: 'unmuted' | 'muted';
}

// ── Machine Events ──

export type MachineEvent =
  // Turn arbiter
  | { type: 'TURN_ACCUMULATING' }
  | { type: 'TURN_COMMITTED' }
  | { type: 'TURN_CANCELLED' }
  // Request lifecycle
  | { type: 'CHAT_REQUEST_SENT'; generation?: number }
  | { type: 'SSE_FIRST_TOKEN' }
  | { type: 'SSE_TOKEN' }
  | { type: 'SSE_DONE'; fullText?: string }
  | { type: 'SSE_ERROR'; error?: string }
  | { type: 'CHAT_ABORTED' }
  | { type: 'ABORT' }
  | { type: 'SAFETY_TIMEOUT' }
  // Playback
  | { type: 'TTS_SEGMENT_START' }
  | { type: 'TTS_SEGMENT_DONE' }
  | { type: 'TTS_QUEUE_EMPTY' }
  | { type: 'TTS_CANCEL'; reason?: string }
  // Input
  | { type: 'INPUT_ACTIVATE' }
  | { type: 'INPUT_DEACTIVATE' }
  | { type: 'SPEECH_START' }
  | { type: 'SPEECH_END' }
  | { type: 'INPUT_PROCESSING' }
  | { type: 'STT_RESULT'; text?: string }
  | { type: 'STT_EMPTY' }
  | { type: 'STT_ERROR' }
  | { type: 'GRACE_TIMEOUT' }
  // Mic mode
  | { type: 'TOGGLE_MIC_MUTE' }
  | { type: 'VOICE_COMMAND_MUTE' }
  | { type: 'VOICE_COMMAND_LISTEN' }
  // Speaker mode
  | { type: 'TOGGLE_SPEAKER_MUTE' }
  | { type: 'VOICE_COMMAND_SILENCE' }
  | { type: 'VOICE_COMMAND_SPEAK' }
  // Connection
  | { type: 'WS_CONNECTED' }
  | { type: 'WS_DISCONNECTED' }
  // Activation
  | { type: 'APP_ACTIVATED' }
  | { type: 'APP_DEACTIVATED' }
  // Context-only updates (root-level)
  | { type: 'VOICE_INTERRUPT_TOGGLE'; value: boolean }
  | { type: 'TTS_DEFER_ON' }
  | { type: 'TTS_DEFER_OFF' }
  | { type: 'TTS_SEGMENT_DEFER'; text: string }
  | { type: 'TTS_DEFERRED_FLUSH' }
  | { type: 'GRACE_DISCARD_SET' }
  | { type: 'GRACE_DISCARD_CLEAR' }
  | { type: 'REMOTE_STOP' }
  | { type: 'REMOTE_STOP_CLEAR' }
  | { type: 'TTS_INTERRUPTED' }
  | { type: 'FILE_UPLOAD' }
  | { type: 'TEXT_SUBMIT'; text?: string };

// ── Initial context from localStorage ──

function getInitialContext(): MachineContext {
  return {
    chatGeneration: 0,
    ttsInterruptedGeneration: -1,
    stoppedByRemote: false,
    requestStartedAt: 0,
    ttsQueueLength: 0,
    ttsAutoPlayDeferred: false,
    deferredTtsTexts: [],
    voiceInterrupt: readString(STORAGE_KEYS.VOICE_INTERRUPT) === 'true',
    graceDiscardAudio: false,
    _micMode: readString(STORAGE_KEYS.MIC_MUTED) === 'true' ? 'commandOnly' : 'open',
    _speakerMode: readString(STORAGE_KEYS.SPEAKER_MUTED) === 'true' ? 'muted' : 'unmuted',
  };
}

// ── Machine Definition ──

export const voiceAppMachine = setup({
  types: {
    context: {} as MachineContext,
    events: {} as MachineEvent,
  },
  guards: {
    isNotCommandOnly: ({ context }) => context._micMode !== 'commandOnly',
    isCommandOnly: ({ context }) => context._micMode === 'commandOnly',
    isSpeakerUnmuted: ({ context }) => context._speakerMode !== 'muted',
    isSpeakerMuted: ({ context }) => context._speakerMode === 'muted',
    voiceInterruptOn: ({ context }) => context.voiceInterrupt === true,
    queueNotEmpty: ({ context }) => context.ttsQueueLength > 0,
    queueEmpty: ({ context }) => context.ttsQueueLength === 0,
    isTTSNotInterrupted: ({ context }) =>
      context.ttsInterruptedGeneration < 0 ||
      context.ttsInterruptedGeneration !== context.chatGeneration,
  },
  actions: {
    syncMicMode: ({ context }) => ({
      ...context,
      _micMode: context._micMode === 'open' ? 'commandOnly' as const : 'open' as const,
    }),
    syncSpeakerMode: ({ context }) => ({
      ...context,
      _speakerMode: context._speakerMode === 'unmuted' ? 'muted' as const : 'unmuted' as const,
    }),
  },
}).createMachine({
  id: 'voiceApp',
  type: 'parallel',
  context: getInitialContext(),

  // Root-level events: context updates that don't change state
  on: {
    VOICE_INTERRUPT_TOGGLE: {
      actions: assign({
        voiceInterrupt: ({ event }) => event.value ?? false,
      }),
    },
    TTS_DEFER_ON: {
      actions: assign({ ttsAutoPlayDeferred: true }),
    },
    TTS_DEFER_OFF: {
      actions: assign({ ttsAutoPlayDeferred: false }),
    },
    TTS_SEGMENT_DEFER: {
      actions: assign({
        deferredTtsTexts: ({ context, event }) => [
          ...context.deferredTtsTexts,
          (event as { text: string }).text,
        ],
      }),
    },
    TTS_DEFERRED_FLUSH: {
      actions: assign({ deferredTtsTexts: [] }),
    },
    GRACE_DISCARD_SET: {
      actions: assign({ graceDiscardAudio: true }),
    },
    GRACE_DISCARD_CLEAR: {
      actions: assign({ graceDiscardAudio: false }),
    },
    REMOTE_STOP: {
      actions: assign({ stoppedByRemote: true }),
    },
    REMOTE_STOP_CLEAR: {
      actions: assign({ stoppedByRemote: false }),
    },
    TTS_INTERRUPTED: {
      actions: assign({
        ttsInterruptedGeneration: ({ context }) => context.chatGeneration,
      }),
    },
  },

  states: {
    // ═══════════════════════════════════════
    // Region 1: Request lifecycle
    // ═══════════════════════════════════════
    request: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            CHAT_REQUEST_SENT: { target: 'thinking' },
          },
        },
        thinking: {
          on: {
            SSE_FIRST_TOKEN: { target: 'streaming' },
            SSE_DONE: { target: 'idle' },
            SSE_ERROR: { target: 'idle' },
            CHAT_ABORTED: { target: 'idle' },
            ABORT: { target: 'idle' },
            SAFETY_TIMEOUT: { target: 'idle' },
          },
          after: { 60000: { target: 'idle' } },
        },
        streaming: {
          on: {
            SSE_TOKEN: {},
            SSE_DONE: { target: 'idle' },
            SSE_ERROR: { target: 'idle' },
            CHAT_ABORTED: { target: 'idle' },
            ABORT: { target: 'idle' },
            SAFETY_TIMEOUT: { target: 'idle' },
          },
          after: { 60000: { target: 'idle' } },
        },
      },
    },

    // ═══════════════════════════════════════
    // Region 2: TTS Playback
    // ═══════════════════════════════════════
    playback: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            TTS_SEGMENT_START: {
              // Guard: only transition to playing if user is NOT speaking.
              // When speech is detected in the input region, TTS playback
              // is deferred — the speak() caller queues text via TTS_SEGMENT_DEFER
              // and it's flushed when SPEECH_END fires.
              guard: not(stateIn({ input: 'speechDetected' })),
              target: 'playing',
            },
          },
        },
        playing: {
          on: {
            TTS_QUEUE_EMPTY: { target: 'idle' },
            TTS_CANCEL: { target: 'idle' },
            TTS_SEGMENT_DONE: {},
          },
        },
      },
    },

    // ═══════════════════════════════════════
    // Region 3: Input / VAD
    // ═══════════════════════════════════════
    input: {
      initial: 'inactive',
      states: {
        inactive: {
          on: {
            INPUT_ACTIVATE: { target: 'idle' },
          },
        },
        idle: {
          on: {
            SPEECH_START: {
              target: 'speechDetected',
              actions: assign({ ttsAutoPlayDeferred: true }),
            },
            INPUT_DEACTIVATE: { target: 'inactive' },
            INPUT_PROCESSING: { target: 'processing' },
            // Enter accumulating from idle when the turn arbiter starts a draft
            TURN_ACCUMULATING: { target: 'accumulating' },
          },
        },
        speechDetected: {
          on: {
            SPEECH_END: {
              target: 'accumulating',
              // NOTE: ttsAutoPlayDeferred stays true during accumulation — only
              // released on commit/cancel. This is the turn-scoped deferral.
            },
            // Handle TURN_CANCELLED / TURN_COMMITTED from speechDetected to
            // fix a race where useVoice fires onSegment("") / onCancel()
            // (which sends TURN_CANCELLED) before the App effect emits
            // SPEECH_END.  Without this, the event is ignored, and the
            // subsequent SPEECH_END transitions to accumulating with no
            // way out — the app gets stuck until the 10s safety timeout.
            TURN_CANCELLED: {
              target: 'idle',
              actions: assign({ ttsAutoPlayDeferred: false }),
            },
            TURN_COMMITTED: {
              target: 'idle',
              actions: assign({ ttsAutoPlayDeferred: false }),
            },
            GRACE_TIMEOUT: {},
          },
        },
        accumulating: {
          on: {
            SPEECH_START: {
              target: 'speechDetected',
              // User started speaking again within the turn
              actions: assign({ ttsAutoPlayDeferred: true }),
            },
            TURN_COMMITTED: {
              target: 'idle',
              actions: assign({ ttsAutoPlayDeferred: false }),
            },
            TURN_CANCELLED: {
              target: 'idle',
              actions: assign({ ttsAutoPlayDeferred: false }),
            },
            INPUT_DEACTIVATE: { target: 'inactive' },
          },
          // Safety timeout: if arbiter somehow stalls, return to idle after 10s
          after: { 10000: { target: 'idle', actions: assign({ ttsAutoPlayDeferred: false }) } },
        },
        processing: {
          on: {
            STT_RESULT: { target: 'idle' },
            STT_EMPTY: { target: 'idle' },
            STT_ERROR: { target: 'idle' },
            SAFETY_TIMEOUT: { target: 'idle' },
          },
          after: { 30000: { target: 'idle' } },
        },
      },
    },

    // ═══════════════════════════════════════
    // Region 4: Mic Mode
    // ═══════════════════════════════════════
    micMode: {
      initial: readString(STORAGE_KEYS.MIC_MUTED) === 'true' ? 'commandOnly' : 'open',
      states: {
        open: {
          on: {
            TOGGLE_MIC_MUTE: { target: 'commandOnly' },
            VOICE_COMMAND_MUTE: { target: 'commandOnly' },
          },
        },
        commandOnly: {
          on: {
            TOGGLE_MIC_MUTE: { target: 'open' },
            VOICE_COMMAND_LISTEN: { target: 'open' },
          },
        },
      },
    },

    // ═══════════════════════════════════════
    // Region 5: Speaker Mode
    // ═══════════════════════════════════════
    speakerMode: {
      initial: readString(STORAGE_KEYS.SPEAKER_MUTED) === 'true' ? 'muted' : 'unmuted',
      states: {
        unmuted: {
          on: {
            TOGGLE_SPEAKER_MUTE: { target: 'muted' },
            VOICE_COMMAND_SILENCE: { target: 'muted' },
          },
        },
        muted: {
          on: {
            TOGGLE_SPEAKER_MUTE: { target: 'unmuted' },
            VOICE_COMMAND_SPEAK: { target: 'unmuted' },
          },
        },
      },
    },

    // ═══════════════════════════════════════
    // Region 6: Connection
    // ═══════════════════════════════════════
    connection: {
      initial: 'disconnected',
      states: {
        disconnected: {
          on: {
            WS_CONNECTED: { target: 'connected' },
          },
        },
        connected: {
          on: {
            WS_DISCONNECTED: { target: 'disconnected' },
          },
        },
      },
    },

    // ═══════════════════════════════════════
    // Region 7: Activation gate
    // ═══════════════════════════════════════
    activation: {
      initial: 'inactive',
      states: {
        inactive: {
          on: {
            APP_ACTIVATED: { target: 'active' },
          },
        },
        active: {
          on: {
            APP_DEACTIVATED: { target: 'inactive' },
          },
        },
      },
    },
  },
});

// ── Helper functions to derive display state from parallel regions ──

export type DisplayState =
  | 'idle'
  | 'listening'
  | 'accumulating'
  | 'thinking'
  | 'streaming'
  | 'speaking';

/** Derive display state from the parallel region snapshot */
export function deriveDisplayState(value: Record<string, string>): DisplayState {
  if (value.activation === 'inactive') return 'idle';
  if (value.input === 'inactive') return 'idle';
  if (value.playback === 'playing') return 'speaking';
  if (value.request === 'thinking') return 'thinking';
  if (value.request === 'streaming') return 'streaming';
  if (value.input === 'processing') return 'thinking';
  if (value.input === 'accumulating') return 'accumulating';
  return 'listening';
}

/** Get status text for a display state */
export function getStatusText(display: DisplayState): string {
  switch (display) {
    case 'idle':
      return 'Tap to start';
    case 'listening':
      return 'Listening...';
    case 'accumulating':
      return 'Composing...';
    case 'thinking':
      return 'Thinking...';
    case 'streaming':
      return 'Responding...';
    case 'speaking':
      return 'Speaking...';
  }
}
